import { useCallback, useEffect, useMemo, useState } from 'react';

import { Hub } from 'aws-amplify';

import { useGetLoyaltyCardDetailsQuery, useGetLoyaltyCardsQuery } from 'generated/rbi-graphql';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IActiveCardDetails } from 'state/loyalty/types';
import LocalStorage from 'utils/cognito/storage';
import { StorageKeys } from 'utils/local-storage';

import { CapsuleChannels, CapsuleEvents, ICapsule } from '../../types/aws-amplify';

import { LOYALTY_ERROR_PLACEHOLDER } from './constants';

const getPreloadedActiveCard = () => LocalStorage.getItem(StorageKeys.LOYALTY_CARD);

const getLoyaltyPointsFraction = (collectedPoints: number, totalPoints: number) => {
  return `${collectedPoints}/${totalPoints}`;
};

export const useLoyaltyActiveCard = () => {
  const { isAuthenticated } = useAuthContext();
  const loyaltyCardDetailsPollIntervalMs = +useFlag(
    LaunchDarklyFlag.LOYALTY_CARD_DETAILS_POLL_INTERVAL_MS
  );
  const [activeCard, setActiveCard] = useState(getPreloadedActiveCard());

  const isMounted = useIsComponentMounted();

  const userIsAuthenticated = isAuthenticated();

  const {
    loading: loadingCards,
    error: errorCards,
    refetch: refetchCards,
    data: cardsData,
  } = useGetLoyaltyCardsQuery({
    skip: !userIsAuthenticated,
  });

  const {
    loading: loadingCardDetails,
    error: errorCardDetails,
    data: cardDetailsData,
    refetch: refetchActiveCard,
  } = useGetLoyaltyCardDetailsQuery({
    variables: {
      cardId: activeCard ? activeCard.cardId : '',
    },
    skip: !activeCard || !userIsAuthenticated,
    pollInterval: userIsAuthenticated ? loyaltyCardDetailsPollIntervalMs || 0 : 0,
  });

  const cardDetails = useMemo(() => cardDetailsData?.getLoyaltyCardDetails || null, [
    cardDetailsData,
  ]);

  const loyaltyDetails: IActiveCardDetails | null = useMemo(() => {
    if (!cardDetails) {
      return null;
    }

    const percent: number =
      cardDetails.numberOfTransactionsMadeTowardsNextReward /
      cardDetails.numberOfTransactionsNeeded;
    return {
      collectedPoints: cardDetails.numberOfTransactionsMadeTowardsNextReward,
      totalPoints: cardDetails.numberOfTransactionsNeeded,
      availableRedemptions: cardDetails.availableRedemptions,
      pointsUntil:
        cardDetails.numberOfTransactionsNeeded -
        cardDetails.numberOfTransactionsMadeTowardsNextReward,
      points: cardDetails.points || 0,
      percentage: percent ? Math.round(percent) * 100 : 0,
      canRedeemReward: cardDetails.canRedeemReward,
    };
  }, [cardDetails]);

  const loyaltyPointsFraction = loyaltyDetails
    ? getLoyaltyPointsFraction(loyaltyDetails.collectedPoints, loyaltyDetails.totalPoints)
    : LOYALTY_ERROR_PLACEHOLDER;

  useEffect(() => {
    if (cardsData?.getLoyaltyCards?.cards?.[0]) {
      const activeCardFromQuery = cardsData.getLoyaltyCards.cards[0];
      setActiveCard(activeCardFromQuery);
      LocalStorage.setItem(StorageKeys.LOYALTY_CARD, activeCardFromQuery);
    }
  }, [cardsData]);

  const loyaltyAuthEventHandler = useCallback(
    (capsule: ICapsule) => {
      if (capsule.payload.event !== CapsuleEvents.SIGN_OUT) {
        return;
      }

      if (isMounted.current) {
        setActiveCard(null);
      }

      LocalStorage.setItem(StorageKeys.LOYALTY_CARD, null);
    },
    // isMounted is a ref and its identity should never update
    [isMounted]
  );

  useEffect(() => {
    Hub.listen(CapsuleChannels.AUTH, loyaltyAuthEventHandler);

    return () => Hub.remove(CapsuleChannels.AUTH, loyaltyAuthEventHandler);
  }, [loyaltyAuthEventHandler]);

  return {
    refetchCards: userIsAuthenticated ? refetchCards : () => Promise.resolve(),
    refetchActiveCard: userIsAuthenticated ? refetchActiveCard : () => Promise.resolve(),
    loading: loadingCards && loadingCardDetails,
    error: errorCards || errorCardDetails,
    activeCard,
    loyaltyDetails,
    loyaltyPointsFraction,
  };
};
